import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, 
  Zap, 
  Clock, 
  TrendingUp,
  Wallet,
  Battery,
  Calendar,
  ArrowRight,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResidentDashboardPage() {
  const { user, isSimulationActive, getEffectiveRole, stopRoleSimulation } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    card: null,
    recentSessions: [],
    totalSpent: 0,
    totalEnergy: 0,
    totalSessions: 0
  });
  
  // Simülasyon kontrolü
  const simulationActive = isSimulationActive ? isSimulationActive() : false;
  
  // Simülasyonu bitir ve dashboard'a yönlendir
  const handleStopSimulation = () => {
    if (stopRoleSimulation) {
      stopRoleSimulation();
      navigate('/dashboard');
    }
  };
  
  // Hoş geldiniz mesajı için isim belirleme
  const getDisplayName = () => {
    if (simulationActive) {
      return 'Site Sakini (Simülasyon)';
    }
    return user?.full_name || user?.email || 'Site Sakini';
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hims_token');
      
      // Kullanıcının RFID kartını al
      const cardsResponse = await fetch(`${API_URL}/api/rfid/user/${user?.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let card = null;
      if (cardsResponse.ok) {
        const cards = await cardsResponse.json();
        card = cards?.[0] || null;
      }

      // Kullanıcının şarj oturumlarını al
      const sessionsResponse = await fetch(`${API_URL}/api/sessions?user_id=${user?.id}&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let recentSessions = [];
      let totalSpent = 0;
      let totalEnergy = 0;
      let totalSessions = 0;

      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        recentSessions = sessionsData?.sessions || sessionsData || [];
        
        // Toplam istatistikleri hesapla
        if (Array.isArray(recentSessions)) {
          totalSessions = recentSessions.length;
          recentSessions.forEach(session => {
            totalSpent += session.cost || 0;
            totalEnergy += session.energy_kwh || 0;
          });
        }
      }

      setDashboardData({
        card,
        recentSessions: recentSessions.slice(0, 5),
        totalSpent,
        totalEnergy,
        totalSessions
      });
    } catch (err) {
      console.error('Dashboard verisi yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user?.id, fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const { card, recentSessions, totalSpent, totalEnergy, totalSessions } = dashboardData;

  // Simülasyon modunda istatistikleri 0 olarak göster
  const displayTotalSessions = simulationActive ? 0 : totalSessions;
  const displayTotalEnergy = simulationActive ? 0 : totalEnergy;
  const displayTotalSpent = simulationActive ? 0 : totalSpent;
  const displayCard = simulationActive ? null : card;
  const displayRecentSessions = simulationActive ? [] : recentSessions;

  return (
    <div className="space-y-6" data-testid="resident-dashboard">
      {/* Simülasyon Modu Banner */}
      {simulationActive && (
        <div className="bg-red-600 text-white py-3 px-4 rounded-xl flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 animate-pulse" />
            <span className="font-bold">SİMÜLASYON MODU:</span>
            <span className="font-medium">Site Sakini görünümü</span>
          </div>
          <button
            onClick={handleStopSimulation}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
            data-testid="stop-simulation-resident-btn"
          >
            <EyeOff className="w-4 h-4" />
            Simülasyonu Bitir
          </button>
        </div>
      )}

      {/* Hoşgeldin Mesajı */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Hoş Geldiniz, {getDisplayName()}!
        </h1>
        <p className="text-blue-100">
          {user?.apartment_number ? `Daire ${user.apartment_number}` : 'Site Sakini Paneli'}
        </p>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Bakiye Kartı */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            {displayCard?.card_type === 'postpaid' && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Faturalı</span>
            )}
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Mevcut Bakiye</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-white">
            {displayCard ? `${(displayCard.balance || 0).toFixed(2)} TL` : (simulationActive ? '0.00 TL' : '-')}
          </p>
          {displayCard?.card_id && (
            <p className="text-xs text-slate-400 mt-2 font-mono">{displayCard.card_id}</p>
          )}
        </div>

        {/* Toplam Şarj */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Toplam Şarj</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-white">
            {displayTotalSessions} Oturum
          </p>
          <p className="text-xs text-slate-400 mt-2">{displayTotalEnergy.toFixed(2)} kWh</p>
        </div>

        {/* Toplam Harcama */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Toplam Harcama</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-white">
            {displayTotalSpent.toFixed(2)} TL
          </p>
        </div>

        {/* Kart Durumu */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <CreditCard className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Kart Durumu</p>
          {displayCard ? (
            <p className={`text-lg font-semibold ${displayCard.is_active || displayCard.status === 'active' ? 'text-emerald-600' : 'text-red-600'}`}>
              {displayCard.is_active || displayCard.status === 'active' ? 'Aktif' : 'Bloke'}
            </p>
          ) : (
            <p className="text-lg font-semibold text-slate-400">{simulationActive ? 'Simülasyon' : 'Kart Yok'}</p>
          )}
        </div>
      </div>

      {/* Son Şarj Oturumları */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Son Şarj Oturumları</h2>
          </div>
          <Link 
            to="/resident/charging-history" 
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            Tümünü Gör <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="p-5">
          {displayRecentSessions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Battery className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{simulationActive ? 'Simülasyon modunda veri gösterilmez' : 'Henüz şarj oturumu bulunmuyor'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayRecentSessions.map((session, index) => (
                <div 
                  key={session.id || index}
                  className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 dark:text-white">
                        {session.station_name || session.charge_point_id || 'İstasyon'}
                      </p>
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <Calendar className="w-3 h-3" />
                        {session.start_time ? new Date(session.start_time).toLocaleDateString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-800 dark:text-white">
                      {(session.energy_kwh || 0).toFixed(2)} kWh
                    </p>
                    <p className="text-sm text-emerald-600 font-medium">
                      {(session.cost || 0).toFixed(2)} TL
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hızlı Erişim */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link 
          to="/resident/balance"
          className="flex items-center gap-4 p-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors group"
        >
          <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl group-hover:scale-110 transition-transform">
            <Wallet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-white">Bakiye Yükle</h3>
            <p className="text-sm text-slate-500">Bakiye yükleme talebi gönder</p>
          </div>
        </Link>

        <Link 
          to="/resident/charging-history"
          className="flex items-center gap-4 p-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors group"
        >
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl group-hover:scale-110 transition-transform">
            <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-white">Şarj Geçmişi</h3>
            <p className="text-sm text-slate-500">Tüm şarj oturumlarını görüntüle</p>
          </div>
        </Link>

        <Link 
          to="/resident/profile"
          className="flex items-center gap-4 p-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors group"
        >
          <div className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-xl group-hover:scale-110 transition-transform">
            <CreditCard className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-white">Profilim</h3>
            <p className="text-sm text-slate-500">Bilgilerini düzenle</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
